import { isoWeekdayFromDateKey } from '@zag/calendar/models';
import { firstSharedOccurrence, isValidOnDate, type OccurrenceWindow, validDatesInRange } from '@zag/common/validity';
import { dayKeyToUtcMs, MS_PER_DAY } from '../day-key-math';
import { resolveWorkloadHoursPerDay } from '../workload-resolver';
import { ALL_WORKDAYS_MASK, countWorkdays, isoDaysToWorkdayMask, isWorkday, isWorkdayBitSet, workdayMaskToIsoDays } from '../workday-mask';
import type { EmploymentCollision, EmploymentDefaultShift, EmploymentRow, IsoWeekday } from './employment.model';

export const DEFAULT_WORKING_DAYS_PER_WEEK = 5;
export const DEFAULT_FULL_TIME_HOURS_PER_WEEK = 40;
const DAYS_PER_WEEK = 7;

export type WorkingPeriod = Pick<EmploymentRow, 'startDate' | 'endDate' | 'rrule' | 'workdayMask'>;

export function employmentWindow(row: WorkingPeriod): OccurrenceWindow {
  return { start: row.startDate, end: row.endDate, rrule: row.rrule, weekdays: workdayMaskToIsoDays(row.workdayMask) };
}

export function isEmployedOn(rows: readonly EmploymentRow[], date: string): boolean {
  return rowsSpanning(rows, date).length > 0;
}

export function isWorkingPeriodOn(row: WorkingPeriod, date: string): boolean {
  return isWorkday(row.workdayMask, date) && isValidOnDate(employmentWindow(row), date);
}

export function isPositionActiveOn(row: { validFrom: string | null; validTo: string | null; rrule: string | null }, date: string): boolean {
  return isValidOnDate({ start: row.validFrom, end: row.validTo, rrule: row.rrule }, date);
}

export function earliestWorkingPeriodOn<T extends WorkingPeriod>(periods: readonly T[], date: string): T | undefined {
  return periods.filter(period => isWorkingPeriodOn(period, date)).sort(compareStartDate)[0];
}

export function earliestWorkingPeriodByDate<T extends WorkingPeriod>(periods: readonly T[], from: string, to: string): Map<string, T> {
  return foldEarliestByDate(periods, () => ({ from, to }));
}

export function earliestAccruedWorkingPeriodByDate<T extends WorkingPeriod & { accrualStartDate?: string | null }>(
  periods: readonly T[],
  from: string,
  to: string
): Map<string, T> {
  return foldEarliestByDate(periods, period => {
    const accrualStart = period.accrualStartDate ?? period.startDate;
    return { from: accrualStart > from ? accrualStart : from, to };
  });
}

export function resolveWorkingRow(rows: readonly EmploymentRow[], date: string): EmploymentRow | undefined {
  const workingRows = rows.filter(row => isWorkingPeriodOn(row, date));
  if (workingRows.length > 1) console.warn(`Employment rows overlap on ${date}; using the earliest startDate`);
  const [earliest] = workingRows.sort(compareRowPrecedence);
  return earliest;
}

export function isWorkingOn(rows: readonly EmploymentRow[], date: string): boolean {
  return resolveWorkingRow(rows, date) !== undefined;
}

export function rowHoursPerDay(row: EmploymentRow, fullTimeHoursPerWeek: number): number {
  return resolveWorkloadHoursPerDay(
    row.workloadHoursPerDay,
    row.workloadPercent,
    fullTimeHoursPerWeek,
    countWorkdays(row.workdayMask) || DEFAULT_WORKING_DAYS_PER_WEEK
  );
}

export function hoursOn(rows: readonly EmploymentRow[], date: string, fullTimeHoursPerWeek: number): number {
  const row = resolveWorkingRow(rows, date);
  return row === undefined ? 0 : rowHoursPerDay(row, fullTimeHoursPerWeek);
}

export function weeklyTargetHours(rows: readonly EmploymentRow[], weekStart: string, fullTimeHoursPerWeek: number): number {
  return weekDates(weekStart).reduce((total, date) => total + hoursOn(rows, date, fullTimeHoursPerWeek), 0);
}

export function templateWorkdayMask(rows: readonly EmploymentRow[], referenceDate: string): number {
  const spanning = rowsSpanning(rows, referenceDate);
  if (spanning.length === 0) return ALL_WORKDAYS_MASK;
  return spanning.reduce((mask, row) => mask | row.workdayMask, 0);
}

export function templateWeeklyTargetHours(rows: readonly EmploymentRow[], referenceDate: string, fullTimeHoursPerWeek: number): number {
  const spanning = rowsSpanning(rows, referenceDate);
  if (spanning.length === 0) return fullTimeHoursPerWeek;
  return spanning.reduce((total, row) => total + rowHoursPerDay(row, fullTimeHoursPerWeek) * countWorkdays(row.workdayMask), 0);
}

export function findEmploymentCollisions(rows: readonly EmploymentRow[], notBefore: string | null = null): EmploymentCollision[] {
  return rowPairs(rows).flatMap(([first, second]) => collisionOf(first, second, notBefore));
}

export function defaultShiftWindow(row: EmploymentRow, weekday: IsoWeekday, shift: EmploymentDefaultShift): OccurrenceWindow {
  return { start: shift.validFrom ?? row.startDate, end: shift.validTo, rrule: shift.rrule, weekdays: [weekday] };
}

export function defaultShiftAppliesOn(row: EmploymentRow, weekday: IsoWeekday, shift: EmploymentDefaultShift, date: string): boolean {
  return (
    isoWeekdayFromDateKey(date) === weekday && isWorkingOn([row], date) && isValidOnDate(defaultShiftWindow(row, weekday, shift), date)
  );
}

export function defaultShiftDatesInRange(
  row: EmploymentRow,
  weekday: IsoWeekday,
  shift: EmploymentDefaultShift,
  from: string,
  to: string
): Set<string> {
  const workingDates = validDatesInRange(employmentWindow(row), from, to);
  const shiftDates = validDatesInRange(defaultShiftWindow(row, weekday, shift), from, to);
  return new Set(
    [...shiftDates].filter(date => workingDates.has(date) && isoWeekdayFromDateKey(date) === weekday && isWorkday(row.workdayMask, date))
  );
}

export function defaultShiftAppliesInTemplate(
  row: EmploymentRow,
  weekday: IsoWeekday,
  shift: EmploymentDefaultShift,
  referenceDate: string
): boolean {
  return (
    isSpanCovering(row.startDate, row.endDate, referenceDate) &&
    isWorkdayBitSet(row.workdayMask, weekday) &&
    isSpanCovering(shift.validFrom ?? row.startDate, shift.validTo, referenceDate)
  );
}

function rowsSpanning(rows: readonly EmploymentRow[], date: string): EmploymentRow[] {
  return rows.filter(row => isSpanCovering(row.startDate, row.endDate, date));
}

function isSpanCovering(start: string | null, end: string | null, date: string): boolean {
  return isValidOnDate({ start, end }, date);
}

function foldEarliestByDate<T extends WorkingPeriod>(
  periods: readonly T[],
  dayRangeOf: (period: T) => { from: string; to: string }
): Map<string, T> {
  const earliestByDate = new Map<string, T>();
  for (const period of [...periods].sort(compareStartDate)) {
    const { from, to } = dayRangeOf(period);
    for (const date of validDatesInRange(employmentWindow(period), from, to)) {
      if (isWorkday(period.workdayMask, date) && !earliestByDate.has(date)) earliestByDate.set(date, period);
    }
  }
  return earliestByDate;
}

function compareStartDate(first: WorkingPeriod, second: WorkingPeriod): number {
  return first.startDate.localeCompare(second.startDate);
}

function compareRowPrecedence(first: EmploymentRow, second: EmploymentRow): number {
  return compareText(first.startDate, second.startDate) || compareText(first.id, second.id);
}

function compareText(first: string, second: string): number {
  if (first === second) return 0;
  return first < second ? -1 : 1;
}

function weekDates(weekStart: string): string[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_, offset) =>
    new Date(dayKeyToUtcMs(weekStart) + offset * MS_PER_DAY).toISOString().slice(0, 10)
  );
}

function rowPairs(rows: readonly EmploymentRow[]): [EmploymentRow, EmploymentRow][] {
  return rows.flatMap((first, index) => rows.slice(index + 1).map((second): [EmploymentRow, EmploymentRow] => [first, second]));
}

function collisionOf(first: EmploymentRow, second: EmploymentRow, notBefore: string | null): EmploymentCollision[] {
  const shared = firstSharedOccurrence(employmentWindow(first), employmentWindow(second), notBefore);
  if (shared === null) return [];
  return [{ periodIds: [first.id, second.id], firstDate: shared.firstDate, weekdays: toIsoWeekdays(shared.weekdays) }];
}

function toIsoWeekdays(weekdays: readonly number[]): IsoWeekday[] {
  return workdayMaskToIsoDays(isoDaysToWorkdayMask(weekdays));
}
